import type { RestrictionTypeEnum } from "~/api/generated/model";
import {
  propertyMapUrl,
  propertyMapLegendUrl,
} from "~/api/manual/property-map";
import type { LayerRegistry } from "~/components/map/registry/mapLayer";
import {
  wfsUrl,
  wmsTileUrl,
  wmsLegendUrl,
} from "~/components/map/registry/ogcUrl";
import { restrictionPropertyLayer } from "../restrictionPropertyLayer";

export const PROPERTY_RESTRICTION_LAYER_IDS: Partial<
  Record<RestrictionTypeEnum, string>
> = {
  soe_beskyttelse: "protection.2",
  vandloeb_beskyt: "protection.3",
  skovbyggelinjer: "protection.4",
  kirkebyggelinjer: "protection.5",
  fredede_omr: "protection.6",
  fredede_omr_forslag_natur: "protection.7",
  habitat_omr: "protection.11",
  ramsar_omr: "protection.12",
  natur_vildt_reservat: "protection.13",
  Natura_2000: "protection.20",
  jordforurening_v1: "pollution.1",
  jordforurening_v2: "pollution.2",
  jordforurening_nuanceret: "pollution.3",
  fundogfortidsminder_areal_beskyttelse: "heritage.2",
  fundogfortidsminder_areal_fredet: "heritage.3",
  bygninger: "heritage.12",
  bes_vandloeb: "bes_vandloeb",
  bes_naturtyper: "bes_naturtyper",
};

export const PROPERTY_LAYER_REGISTRY: LayerRegistry = {
  layers: [
    {
      ...{
        id: "backgrounds.1",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.backgrounds1.name",
        kind: "wms",
      },
      source: { url: propertyMapUrl("backgrounds.1", "wms") },
    },
    {
      ...{
        id: "backgrounds.2",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.backgrounds2.name",
        kind: "wms",
      },
      source: { url: propertyMapUrl("backgrounds.2", "wms") },
    },
    {
      ...{
        id: "backgrounds.3",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.backgrounds3.name",
        kind: "wms",
      },
      source: { url: propertyMapUrl("backgrounds.3", "wms") },
    },
    {
      ...{
        id: "backgrounds.4",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.backgrounds4.name",
        descriptionKey: "properties:map.layers.backgrounds4.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("backgrounds.4", "wms") },
    },
    {
      ...{
        id: "ortofoto.1",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto1.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2025,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.1", "wms") },
    },
    {
      ...{
        id: "ortofoto.2",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto2.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2024,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.2", "wms") },
    },
    {
      ...{
        id: "ortofoto.3",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto3.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2023,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.3", "wms") },
    },
    {
      ...{
        id: "ortofoto.4",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto4.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2022,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.4", "wms") },
    },
    {
      ...{
        id: "ortofoto.5",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto5.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2021,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.5", "wms") },
    },
    {
      ...{
        id: "ortofoto.6",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto6.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2020,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.6", "wms") },
    },
    {
      ...{
        id: "ortofoto.7",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto7.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2019,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.7", "wms") },
    },
    {
      ...{
        id: "ortofoto.8",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto8.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2018,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.8", "wms") },
    },
    {
      ...{
        id: "ortofoto.9",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto9.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2017,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.9", "wms") },
    },
    {
      ...{
        id: "ortofoto.10",
        role: "background",
        minZoom: 0,
        nameKey: "properties:map.layers.ortofoto10.name",
        kind: "wms",
        selectionGroup: "ortofoto",
        year: 2016,
        selectionLabelKey: "properties:map.groups.ortofoto",
      },
      source: { url: propertyMapUrl("ortofoto.10", "wms") },
    },
    {
      ...{
        id: "geodanmark.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark1.name",
        descriptionKey: "properties:map.layers.geodanmark1.description",
        kind: "wms",
        legendColor: "#969696",
      },
      source: { url: propertyMapUrl("geodanmark.1", "wms") },
    },
    {
      ...{
        id: "geodanmark.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark2.name",
        descriptionKey: "properties:map.layers.geodanmark2.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.2", "wfs") },
    },
    {
      ...{
        id: "geodanmark.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark3.name",
        descriptionKey: "properties:map.layers.geodanmark3.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.3", "wfs") },
    },
    {
      ...{
        id: "geodanmark.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark4.name",
        descriptionKey: "properties:map.layers.geodanmark4.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.4", "wfs") },
    },
    {
      ...{
        id: "geodanmark.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark5.name",
        descriptionKey: "properties:map.layers.geodanmark5.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.5", "wfs") },
    },
    {
      ...{
        id: "geodanmark.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark6.name",
        descriptionKey: "properties:map.layers.geodanmark6.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.6", "wfs") },
    },
    {
      ...{
        id: "geodanmark.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark7.name",
        descriptionKey: "properties:map.layers.geodanmark7.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.7", "wfs") },
    },
    {
      ...{
        id: "geodanmark.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark8.name",
        descriptionKey: "properties:map.layers.geodanmark8.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.8", "wfs") },
    },
    {
      ...{
        id: "geodanmark.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark9.name",
        descriptionKey: "properties:map.layers.geodanmark9.description",
        kind: "wms",
        legendColor: "#FF8000",
      },
      source: { url: propertyMapUrl("geodanmark.9", "wms") },
    },
    {
      ...{
        id: "geodanmark.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark10.name",
        descriptionKey: "properties:map.layers.geodanmark10.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.10", "wfs") },
    },
    {
      ...{
        id: "geodanmark.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark11.name",
        descriptionKey: "properties:map.layers.geodanmark11.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.11", "wfs") },
    },
    {
      ...{
        id: "geodanmark.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark12.name",
        descriptionKey: "properties:map.layers.geodanmark12.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.12", "wfs") },
    },
    {
      ...{
        id: "geodanmark.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark13.name",
        descriptionKey: "properties:map.layers.geodanmark13.description",
        kind: "wms",
        legendColor: "#00FF00",
      },
      source: { url: propertyMapUrl("geodanmark.13", "wms") },
    },
    {
      ...{
        id: "geodanmark.14",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark14.name",
        descriptionKey: "properties:map.layers.geodanmark14.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.14", "wfs") },
    },
    {
      ...{
        id: "geodanmark.15",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark15.name",
        descriptionKey: "properties:map.layers.geodanmark15.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.15", "wfs") },
    },
    {
      ...{
        id: "geodanmark.16",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark16.name",
        descriptionKey: "properties:map.layers.geodanmark16.description",
        kind: "wms",
        legendColor: "#A94F00",
      },
      source: { url: propertyMapUrl("geodanmark.16", "wms") },
    },
    {
      ...{
        id: "geodanmark.17",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark17.name",
        descriptionKey: "properties:map.layers.geodanmark17.description",
        kind: "wms",
        legendColor: "#007F00",
      },
      source: { url: propertyMapUrl("geodanmark.17", "wms") },
    },
    {
      ...{
        id: "geodanmark.18",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark18.name",
        descriptionKey: "properties:map.layers.geodanmark18.description",
        kind: "wms",
        legendColor: "#007F00",
      },
      source: { url: propertyMapUrl("geodanmark.18", "wms") },
    },
    {
      ...{
        id: "geodanmark.19",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark19.name",
        descriptionKey: "properties:map.layers.geodanmark19.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.19", "wfs") },
    },
    {
      ...{
        id: "geodanmark.20",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark20.name",
        descriptionKey: "properties:map.layers.geodanmark20.description",
        kind: "wms",
        legendColor: "#C0C0C0",
      },
      source: { url: propertyMapUrl("geodanmark.20", "wms") },
    },
    {
      ...{
        id: "geodanmark.21",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark21.name",
        descriptionKey: "properties:map.layers.geodanmark21.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.21", "wfs") },
    },
    {
      ...{
        id: "geodanmark.22",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark22.name",
        descriptionKey: "properties:map.layers.geodanmark22.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.22", "wfs") },
    },
    {
      ...{
        id: "geodanmark.23",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark23.name",
        descriptionKey: "properties:map.layers.geodanmark23.description",
        kind: "wms",
        legendColor: "#99CC00",
      },
      source: { url: propertyMapUrl("geodanmark.23", "wms") },
    },
    {
      ...{
        id: "geodanmark.24",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark24.name",
        descriptionKey: "properties:map.layers.geodanmark24.description",
        kind: "wms",
        legendColor: "#FF0080",
      },
      source: { url: propertyMapUrl("geodanmark.24", "wms") },
    },
    {
      ...{
        id: "geodanmark.25",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark25.name",
        descriptionKey: "properties:map.layers.geodanmark25.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.25", "wfs") },
    },
    {
      ...{
        id: "geodanmark.26",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark26.name",
        descriptionKey: "properties:map.layers.geodanmark26.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.26", "wfs") },
    },
    {
      ...{
        id: "geodanmark.27",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark27.name",
        descriptionKey: "properties:map.layers.geodanmark27.description",
        kind: "wms",
        legendColor: "#373737",
      },
      source: { url: propertyMapUrl("geodanmark.27", "wms") },
    },
    {
      ...{
        id: "geodanmark.28",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark28.name",
        descriptionKey: "properties:map.layers.geodanmark28.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("geodanmark.28", "wms") },
    },
    {
      ...{
        id: "geodanmark.29",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark29.name",
        descriptionKey: "properties:map.layers.geodanmark29.description",
        kind: "wms",
        legendColor: "#66FF00",
      },
      source: { url: propertyMapUrl("geodanmark.29", "wms") },
    },
    {
      ...{
        id: "geodanmark.30",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark30.name",
        descriptionKey: "properties:map.layers.geodanmark30.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.30", "wfs") },
    },
    {
      ...{
        id: "geodanmark.31",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark31.name",
        descriptionKey: "properties:map.layers.geodanmark31.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.31", "wfs") },
    },
    {
      ...{
        id: "geodanmark.32",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark32.name",
        descriptionKey: "properties:map.layers.geodanmark32.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.32", "wfs") },
    },
    {
      ...{
        id: "geodanmark.33",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark33.name",
        descriptionKey: "properties:map.layers.geodanmark33.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.33", "wfs") },
    },
    {
      ...{
        id: "geodanmark.34",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark34.name",
        descriptionKey: "properties:map.layers.geodanmark34.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.34", "wfs") },
    },
    {
      ...{
        id: "geodanmark.35",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark35.name",
        descriptionKey: "properties:map.layers.geodanmark35.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.35", "wfs") },
    },
    {
      ...{
        id: "geodanmark.36",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark36.name",
        descriptionKey: "properties:map.layers.geodanmark36.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.36", "wfs") },
    },
    {
      ...{
        id: "geodanmark.37",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark37.name",
        descriptionKey: "properties:map.layers.geodanmark37.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.37", "wfs") },
    },
    {
      ...{
        id: "geodanmark.38",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark38.name",
        descriptionKey: "properties:map.layers.geodanmark38.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.38", "wfs") },
    },
    {
      ...{
        id: "geodanmark.39",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark39.name",
        descriptionKey: "properties:map.layers.geodanmark39.description",
        kind: "wms",
        legendColor: "#4000FF",
      },
      source: { url: propertyMapUrl("geodanmark.39", "wms") },
    },
    {
      ...{
        id: "geodanmark.40",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark40.name",
        descriptionKey: "properties:map.layers.geodanmark40.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.40", "wfs") },
    },
    {
      ...{
        id: "geodanmark.41",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark41.name",
        descriptionKey: "properties:map.layers.geodanmark41.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.41", "wfs") },
    },
    {
      ...{
        id: "geodanmark.42",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark42.name",
        descriptionKey: "properties:map.layers.geodanmark42.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.42", "wfs") },
    },
    {
      ...{
        id: "geodanmark.43",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark43.name",
        descriptionKey: "properties:map.layers.geodanmark43.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.43", "wfs") },
    },
    {
      ...{
        id: "geodanmark.44",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark44.name",
        descriptionKey: "properties:map.layers.geodanmark44.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.44", "wfs") },
    },
    {
      ...{
        id: "geodanmark.45",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark45.name",
        descriptionKey: "properties:map.layers.geodanmark45.description",
        kind: "wms",
        legendColor: "#FF0000",
      },
      source: { url: propertyMapUrl("geodanmark.45", "wms") },
    },
    {
      ...{
        id: "geodanmark.46",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark46.name",
        descriptionKey: "properties:map.layers.geodanmark46.description",
        kind: "wms",
        legendColor: "#F08CB4",
      },
      source: { url: propertyMapUrl("geodanmark.46", "wms") },
    },
    {
      ...{
        id: "geodanmark.47",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark47.name",
        descriptionKey: "properties:map.layers.geodanmark47.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.47", "wfs") },
    },
    {
      ...{
        id: "geodanmark.48",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark48.name",
        descriptionKey: "properties:map.layers.geodanmark48.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.48", "wfs") },
    },
    {
      ...{
        id: "geodanmark.49",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark49.name",
        descriptionKey: "properties:map.layers.geodanmark49.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.49", "wfs") },
    },
    {
      ...{
        id: "geodanmark.50",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark50.name",
        descriptionKey: "properties:map.layers.geodanmark50.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.50", "wfs") },
    },
    {
      ...{
        id: "geodanmark.51",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark51.name",
        descriptionKey: "properties:map.layers.geodanmark51.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("geodanmark.51", "wms") },
    },
    {
      ...{
        id: "geodanmark.52",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark52.name",
        descriptionKey: "properties:map.layers.geodanmark52.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.52", "wfs") },
    },
    {
      ...{
        id: "geodanmark.53",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark53.name",
        descriptionKey: "properties:map.layers.geodanmark53.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.53", "wfs") },
    },
    {
      ...{
        id: "geodanmark.54",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark54.name",
        descriptionKey: "properties:map.layers.geodanmark54.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.54", "wfs") },
    },
    {
      ...{
        id: "geodanmark.55",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark55.name",
        descriptionKey: "properties:map.layers.geodanmark55.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.55", "wfs") },
    },
    {
      ...{
        id: "geodanmark.56",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark56.name",
        descriptionKey: "properties:map.layers.geodanmark56.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.56", "wfs") },
    },
    {
      ...{
        id: "geodanmark.57",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark57.name",
        descriptionKey: "properties:map.layers.geodanmark57.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.57", "wfs") },
    },
    {
      ...{
        id: "geodanmark.58",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark58.name",
        descriptionKey: "properties:map.layers.geodanmark58.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.58", "wfs") },
    },
    {
      ...{
        id: "geodanmark.59",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark59.name",
        descriptionKey: "properties:map.layers.geodanmark59.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.59", "wfs") },
    },
    {
      ...{
        id: "geodanmark.61",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark61.name",
        descriptionKey: "properties:map.layers.geodanmark61.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.61", "wfs") },
    },
    {
      ...{
        id: "geodanmark.62",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark62.name",
        descriptionKey: "properties:map.layers.geodanmark62.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.62", "wfs") },
    },
    {
      ...{
        id: "geodanmark.63",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark63.name",
        descriptionKey: "properties:map.layers.geodanmark63.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.63", "wfs") },
    },
    {
      ...{
        id: "geodanmark.64",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark64.name",
        descriptionKey: "properties:map.layers.geodanmark64.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.64", "wfs") },
    },
    {
      ...{
        id: "geodanmark.65",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark65.name",
        descriptionKey: "properties:map.layers.geodanmark65.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.65", "wfs") },
    },
    {
      ...{
        id: "geodanmark.66",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.geodanmark66.name",
        descriptionKey: "properties:map.layers.geodanmark66.description",
        kind: "wfs",
        geometryType: "mixed",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: { url: propertyMapUrl("geodanmark.66", "wfs") },
    },
    {
      ...{
        id: "roads.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.roads1.name",
        descriptionKey: "properties:map.layers.roads1.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("roads.1", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("roads.1") }],
    },
    {
      ...{
        id: "roads.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.roads2.name",
        descriptionKey: "properties:map.layers.roads2.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("roads.2", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("roads.2") }],
    },
    {
      ...{
        id: "roads.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.roads3.name",
        descriptionKey: "properties:map.layers.roads3.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("roads.3", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("roads.3") }],
    },
    {
      ...{
        id: "elevation.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation1.name",
        descriptionKey: "properties:map.layers.elevation1.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.1", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.1") }],
    },
    {
      ...{
        id: "elevation.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation2.name",
        descriptionKey: "properties:map.layers.elevation2.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.2", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.2") }],
    },
    {
      ...{
        id: "elevation.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation3.name",
        descriptionKey: "properties:map.layers.elevation3.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.3", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.3") }],
    },
    {
      ...{
        id: "elevation.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation4.name",
        descriptionKey: "properties:map.layers.elevation4.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.4", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.4") }],
    },
    {
      ...{
        id: "elevation.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation5.name",
        descriptionKey: "properties:map.layers.elevation5.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.5", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.5") }],
    },
    {
      ...{
        id: "elevation.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.elevation6.name",
        descriptionKey: "properties:map.layers.elevation6.description",
        kind: "wms",
      },
      source: { url: propertyMapUrl("elevation.6", "wms") },
      legendImages: [{ url: propertyMapLegendUrl("elevation.6") }],
    },
    {
      ...{
        id: "protection.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection1.name",
        descriptionKey: "properties:map.layers.protection1.description",
        kind: "wfs",
        geometryType: "line",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:bes_sten_jorddiger_2022",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("soe_beskyttelse"),
      ...{
        id: "protection.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection2.name",
        descriptionKey: "properties:map.layers.protection2.description",
      },
    },
    {
      ...restrictionPropertyLayer("vandloeb_beskyt"),
      ...{
        id: "protection.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection3.name",
        descriptionKey: "properties:map.layers.protection3.description",
      },
    },
    {
      ...restrictionPropertyLayer("skovbyggelinjer"),
      ...{
        id: "protection.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection4.name",
        descriptionKey: "properties:map.layers.protection4.description",
      },
    },
    {
      ...restrictionPropertyLayer("kirkebyggelinjer"),
      ...{
        id: "protection.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection5.name",
        descriptionKey: "properties:map.layers.protection5.description",
      },
    },
    {
      ...restrictionPropertyLayer("fredede_omr"),
      ...{
        id: "protection.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection6.name",
        descriptionKey: "properties:map.layers.protection6.description",
      },
    },
    {
      ...{
        id: "protection.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection7.name",
        descriptionKey: "properties:map.layers.protection7.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:fredede_omr_forslag",
        ),
      },
    },
    {
      ...{
        id: "protection.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection10.name",
        descriptionKey: "properties:map.layers.protection10.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:fugle_bes_omr",
        ),
      },
    },
    {
      ...{
        id: "protection.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection11.name",
        descriptionKey: "properties:map.layers.protection11.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:habitat_omr",
        ),
      },
    },
    {
      ...{
        id: "protection.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection12.name",
        descriptionKey: "properties:map.layers.protection12.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:ramsar_omr",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("natur_vildt_reservat"),
      ...{
        id: "protection.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection13.name",
        descriptionKey: "properties:map.layers.protection13.description",
      },
    },
    {
      ...{
        id: "protection.14",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection14.name",
        descriptionKey: "properties:map.layers.protection14.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:fredede_omr_bk",
        ),
      },
    },
    {
      ...{
        id: "protection.15",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection15.name",
        descriptionKey: "properties:map.layers.protection15.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:theme-pg-natura2000_omraader_habitatnatur",
        ),
      },
    },
    {
      ...{
        id: "protection.16",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection16.name",
        descriptionKey: "properties:map.layers.protection16.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:theme-kategori1natur",
        ),
      },
    },
    {
      ...{
        id: "protection.17",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection17.name",
        descriptionKey: "properties:map.layers.protection17.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:theme-kategori2natur",
        ),
      },
    },
    {
      ...{
        id: "protection.18",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection18.name",
        descriptionKey: "properties:map.layers.protection18.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:theme-hoejmoser_lobsoer",
        ),
      },
    },
    {
      ...{
        id: "protection.19",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection19.name",
        descriptionKey: "properties:map.layers.protection19.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:theme-kategori3natur",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("Natura_2000"),
      ...{
        id: "protection.20",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection20.name",
        descriptionKey: "properties:map.layers.protection20.description",
      },
    },
    {
      ...{
        id: "protection.21",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.protection21.name",
        descriptionKey: "properties:map.layers.protection21.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/dai/ows",
          "dai:naturmaessig_saerlig_vaerdifuld_skov",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("jordforurening_v1"),
      ...{
        id: "pollution.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.pollution1.name",
        descriptionKey: "properties:map.layers.pollution1.description",
      },
    },
    {
      ...restrictionPropertyLayer("jordforurening_v2"),
      ...{
        id: "pollution.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.pollution2.name",
        descriptionKey: "properties:map.layers.pollution2.description",
      },
    },
    {
      ...restrictionPropertyLayer("jordforurening_nuanceret"),
      ...{
        id: "pollution.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.pollution3.name",
        descriptionKey: "properties:map.layers.pollution3.description",
      },
    },
    {
      ...{
        id: "pollution.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.pollution4.name",
        descriptionKey: "properties:map.layers.pollution4.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:omr_klassificering",
        ),
      },
    },
    {
      ...{
        id: "pollution.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.pollution5.name",
        descriptionKey: "properties:map.layers.pollution5.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs",
          "dai:paabud_jfl",
        ),
      },
    },
    {
      ...{
        id: "noise.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise1.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-88fcdd5e9022.png", label: "Over 74 dB" },
          { url: "/map-legends/noise-e58e002e5113.png", label: "69-74 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "64-69 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "59-64 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "54-59 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.1", "wms") },
    },
    {
      ...{
        id: "noise.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise2.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-e58e002e5113.png", label: "Over 65 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "60-65 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "55-60 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "50-55 dB" },
          { url: "/map-legends/noise-ceeea7e22471.png", label: "45-50 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.2", "wms") },
    },
    {
      ...{
        id: "noise.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise3.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-88fcdd5e9022.png", label: "Over 74 dB" },
          { url: "/map-legends/noise-e58e002e5113.png", label: "69-74 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "64-69 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "59-64 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "54-59 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.3", "wms") },
    },
    {
      ...{
        id: "noise.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise4.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-1c7f027bd75a.png", label: "Over 75 dB" },
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "70-75 dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.4", "wms") },
    },
    {
      ...{
        id: "noise.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise5.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-e58e002e5113.png", label: "Over 65 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "60-65 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "55-60 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "50-55 dB" },
          { url: "/map-legends/noise-ceeea7e22471.png", label: "45-50 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.5", "wms") },
    },
    {
      ...{
        id: "noise.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise6.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "70-> dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
          { url: "/map-legends/noise-6507081074c7.png", label: "50-55 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.6", "wms") },
    },
    {
      ...{
        id: "noise.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise7.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-1c7f027bd75a.png", label: "over 75 dB" },
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "70-75 dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.7", "wms") },
    },
    {
      ...{
        id: "noise.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise8.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "over 70 dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
          { url: "/map-legends/noise-6507081074c7.png", label: "50-55 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.8", "wms") },
    },
    {
      ...{
        id: "noise.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise9.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-88fcdd5e9022.png", label: "Over 73 dB" },
          { url: "/map-legends/noise-e58e002e5113.png", label: "68-73 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "63-68 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "58-63 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "53-58 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.9", "wms") },
    },
    {
      ...{
        id: "noise.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise10.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-e58e002e5113.png", label: "Over 65 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "60-65 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "55-60 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "50-55 dB" },
          { url: "/map-legends/noise-ceeea7e22471.png", label: "45-50 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.10", "wms") },
    },
    {
      ...{
        id: "noise.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise11.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-88fcdd5e9022.png", label: "Over 73 dB" },
          { url: "/map-legends/noise-e58e002e5113.png", label: "68-73 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "63-68 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "58-63 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "53-58 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.11", "wms") },
    },
    {
      ...{
        id: "noise.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise12.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-e58e002e5113.png", label: "Over 65 dB" },
          { url: "/map-legends/noise-724b65e6493e.png", label: "60-65 dB" },
          { url: "/map-legends/noise-c066ae8ba1cf.png", label: "55-60 dB" },
          { url: "/map-legends/noise-8890b8eff362.png", label: "50-55 dB" },
          { url: "/map-legends/noise-ceeea7e22471.png", label: "45-50 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.12", "wms") },
    },
    {
      ...{
        id: "noise.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise13.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-1c7f027bd75a.png", label: " over 75 dB" },
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "70-75 dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.13", "wms") },
    },
    {
      ...{
        id: "noise.14",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.noise14.name",
        kind: "wms",
        legendImages: [
          { url: "/map-legends/noise-3e37c0aa4c2e.png", label: "70-> dB" },
          { url: "/map-legends/noise-b779ba6b7511.png", label: "65-70dB" },
          { url: "/map-legends/noise-a4ea97c4b1dd.png", label: "60-65 dB" },
          { url: "/map-legends/noise-42502f96d22f.png", label: "55-60 dB" },
          { url: "/map-legends/noise-6507081074c7.png", label: "50-55 dB" },
        ],
      },
      source: { url: propertyMapUrl("noise.14", "wms") },
    },
    {
      ...{
        id: "agriculture.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture1.name",
        descriptionKey: "properties:map.layers.agriculture1.description",
        kind: "wms",
        selectionGroup: "Marker:Marker",
        year: 2023,
        selectionLabelKey: "properties:map.layers.agriculture1Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Marker:Marker_2023",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Marker:Marker_2023",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture2.name",
        descriptionKey: "properties:map.layers.agriculture2.description",
        kind: "wms",
        selectionGroup: "Marker:Marker",
        year: 2024,
        selectionLabelKey: "properties:map.layers.agriculture2Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Marker:Marker_2024",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Marker:Marker_2024",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture3.name",
        descriptionKey: "properties:map.layers.agriculture3.description",
        kind: "wms",
        selectionGroup: "Marker:Marker",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture3Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Marker:Marker_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Marker:Marker_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture4.name",
        descriptionKey: "properties:map.layers.agriculture4.description",
        kind: "wms",
        selectionGroup: "Marker:Marker",
        year: 2026,
        selectionLabelKey: "properties:map.layers.agriculture4Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Marker:Marker_2026",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Marker:Marker_2026",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture5.name",
        descriptionKey: "properties:map.layers.agriculture5.description",
        kind: "wms",
        selectionGroup: "Markblokke:Markblokke",
        year: 2024,
        selectionLabelKey: "properties:map.layers.agriculture5Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Markblokke:Markblokke_2024",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Markblokke:Markblokke_2024",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture6.name",
        descriptionKey: "properties:map.layers.agriculture6.description",
        kind: "wms",
        selectionGroup: "Markblokke:Markblokke",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture6Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Markblokke:Markblokke_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Markblokke:Markblokke_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture7.name",
        descriptionKey: "properties:map.layers.agriculture7.description",
        kind: "wms",
        selectionGroup: "Markblokke:Markblokke",
        year: 2026,
        selectionLabelKey: "properties:map.layers.agriculture7Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Markblokke:Markblokke_2026",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Markblokke:Markblokke_2026",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture8.name",
        descriptionKey: "properties:map.layers.agriculture8.description",
        kind: "wms",
        selectionGroup: "Markblokke:Markblokke",
        year: 2027,
        selectionLabelKey: "properties:map.layers.agriculture8Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Markblokke:Markblokke_2027",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Markblokke:Markblokke_2027",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture9.name",
        descriptionKey: "properties:map.layers.agriculture9.description",
        kind: "wms",
        selectionGroup: "Jordbunds_og_terraenforhold:Jordbundskort",
        year: 2024,
        selectionLabelKey: "properties:map.layers.agriculture9Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Jordbunds_og_terraenforhold:Jordbundskort_2024",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Jordbunds_og_terraenforhold:Jordbundskort_2024",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture10.name",
        descriptionKey: "properties:map.layers.agriculture10.description",
        kind: "wms",
        selectionGroup: "Jordbunds_og_terraenforhold:Jordbundskort",
        year: 2019,
        selectionLabelKey: "properties:map.layers.agriculture10Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Jordbunds_og_terraenforhold:Jordbundskort_2019",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Jordbunds_og_terraenforhold:Jordbundskort_2019",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture11.name",
        descriptionKey: "properties:map.layers.agriculture11.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:GLM_Soer",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture11Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:GLM_Soer_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:GLM_Soer_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture12.name",
        descriptionKey: "properties:map.layers.agriculture12.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:GLM_Soer",
        year: 2026,
        selectionLabelKey: "properties:map.layers.agriculture12Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:GLM_Soer_2026",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:GLM_Soer_2026",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture13.name",
        descriptionKey: "properties:map.layers.agriculture13.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:GLM_Soer",
        year: 2027,
        selectionLabelKey: "properties:map.layers.agriculture13Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:GLM_Soer_2027",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:GLM_Soer_2027",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.14",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture14.name",
        kind: "wms",
        selectionGroup: "Naturudpegninger:HNV_1_4",
        year: 2024,
        selectionLabelKey: "properties:map.layers.agriculture14Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Naturudpegninger:HNV_1_4_2024",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Naturudpegninger:HNV_1_4_2024",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.15",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture15.name",
        kind: "wms",
        selectionGroup: "Naturudpegninger:HNV_1_4",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture15Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Naturudpegninger:HNV_1_4_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Naturudpegninger:HNV_1_4_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.16",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture16.name",
        kind: "wms",
        selectionGroup: "Naturudpegninger:HNV_5_13",
        year: 2024,
        selectionLabelKey: "properties:map.layers.agriculture16Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Naturudpegninger:HNV_5_13_2024",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Naturudpegninger:HNV_5_13_2024",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.17",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture17.name",
        kind: "wms",
        selectionGroup: "Naturudpegninger:HNV_5_13",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture17Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Naturudpegninger:HNV_5_13_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Naturudpegninger:HNV_5_13_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.18",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture18.name",
        descriptionKey: "properties:map.layers.agriculture18.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:Fradrag_grundbetaling",
        year: 2025,
        selectionLabelKey: "properties:map.layers.agriculture18Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2025",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2025",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.19",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture19.name",
        descriptionKey: "properties:map.layers.agriculture19.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:Fradrag_grundbetaling",
        year: 2026,
        selectionLabelKey: "properties:map.layers.agriculture19Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2026",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2026",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.20",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture20.name",
        descriptionKey: "properties:map.layers.agriculture20.description",
        kind: "wms",
        selectionGroup: "GB_MFO_og_groenne_krav:Fradrag_grundbetaling",
        year: 2027,
        selectionLabelKey: "properties:map.layers.agriculture20Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2027",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "GB_MFO_og_groenne_krav:Fradrag_grundbetaling_2027",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.21",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture21.name",
        descriptionKey: "properties:map.layers.agriculture21.description",
        kind: "wms",
        selectionGroup: "Vandprojekter:Satskort",
        year: 2014,
        selectionLabelKey: "properties:map.layers.agriculture21Theme.name",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Vandprojekter:Satskort_2014",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geodata.fvm.dk/geoserver/ows",
            "Vandprojekter:Satskort_2014",
          ),
        },
      ],
    },
    {
      ...{
        id: "agriculture.22",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.agriculture22.name",
        descriptionKey: "properties:map.layers.agriculture22.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://arld-extgeo.miljoeportal.dk/geoserver/wms",
          "landbrugsdrift:DJF_LAVBUND",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://arld-extgeo.miljoeportal.dk/geoserver/wms",
            "landbrugsdrift:DJF_LAVBUND",
          ),
        },
      ],
    },
    {
      ...{
        id: "heritage.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage1.name",
        descriptionKey: "properties:map.layers.heritage1.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/ffgeoserver/public/wfs",
          "public:fundogfortidsminder_areal_ka",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("fundogfortidsminder_areal_beskyttelse"),
      ...{
        id: "heritage.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage2.name",
        descriptionKey: "properties:map.layers.heritage2.description",
      },
    },
    {
      ...restrictionPropertyLayer("fundogfortidsminder_areal_fredet"),
      ...{
        id: "heritage.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage3.name",
      },
    },
    {
      ...{
        id: "heritage.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage4.name",
        descriptionKey: "properties:map.layers.heritage4.description",
        kind: "wfs",
        geometryType: "line",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/ffgeoserver/public/wfs",
          "public:fundogfortidsminder_linje_fredet",
        ),
      },
    },
    {
      ...{
        id: "heritage.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage5.name",
        descriptionKey: "properties:map.layers.heritage5.description",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/ffgeoserver/public/wfs",
          "public:fundogfortidsminder_punkt_fredet",
        ),
      },
    },
    {
      ...{
        id: "heritage.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage6.name",
        descriptionKey: "properties:map.layers.heritage6.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/ffgeoserver/public/wfs",
          "public:fundogfortidsminder_areal_alle",
        ),
      },
    },
    {
      ...{
        id: "heritage.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage7.name",
        descriptionKey: "properties:map.layers.heritage7.description",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/ffgeoserver/public/wfs",
          "public:fundogfortidsminder_punkt_ikkefredet",
        ),
      },
    },
    {
      ...{
        id: "heritage.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage8.name",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/geoserver/ows",
          "fbb:view_bygning_fredningstatus_hoej",
        ),
      },
    },
    {
      ...{
        id: "heritage.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage9.name",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/geoserver/ows",
          "fbb:view_bygning_fredningstatus_lav",
        ),
      },
    },
    {
      ...{
        id: "heritage.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage10.name",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/geoserver/ows",
          "fbb:view_bygning_fredningstatus_med",
        ),
      },
    },
    {
      ...{
        id: "heritage.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage11.name",
        kind: "wfs",
        geometryType: "point",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://www.kulturarv.dk/geoserver/ows",
          "fbb:view_bygning_bevaringssag",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("bygninger"),
      ...{
        id: "heritage.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.heritage12.name",
      },
    },
    {
      ...{
        id: "utilities.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities1.name",
        descriptionKey: "properties:map.layers.utilities1.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
        labelAttribute: "vaerdi1203",
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_forsyningomraade_vedtaget_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities2.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_forsyningomraade_aflyst_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities3.name",
        descriptionKey: "properties:map.layers.utilities3.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_tilslutningspligtomraade_vedtaget_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities4.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_tilslutningspligtomraade_aflyst_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities5.name",
        descriptionKey: "properties:map.layers.utilities5.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_forsyningsforbudomraade_vedtaget_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities6.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://geoserver.plandata.dk/geoserver/wfs",
          "pdk:theme_pdk_forsyningsforbudomraade_aflyst_v",
        ),
      },
    },
    {
      ...{
        id: "utilities.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.utilities7.name",
        descriptionKey: "properties:map.layers.utilities7.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
        labelAttribute: "selsk_navn",
      },
      source: { url: propertyMapUrl("utilities.7", "wfs") },
    },
    {
      ...{
        id: "administrative.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative1.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.1", "wfs") },
    },
    {
      ...{
        id: "administrative.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative2.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.2", "wfs") },
    },
    {
      ...{
        id: "administrative.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative3.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.3", "wfs") },
    },
    {
      ...{
        id: "administrative.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative4.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.4", "wfs") },
    },
    {
      ...{
        id: "administrative.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative5.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.5", "wfs") },
    },
    {
      ...{
        id: "administrative.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative6.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.6", "wfs") },
    },
    {
      ...{
        id: "administrative.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative7.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.7", "wfs") },
    },
    {
      ...{
        id: "administrative.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative8.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#9333ea",
          fillOpacity: 0.2,
          lineColor: "#9333ea",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.8", "wfs") },
    },
    {
      ...{
        id: "administrative.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative9.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#ea580c",
          fillOpacity: 0.2,
          lineColor: "#ea580c",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.9", "wfs") },
    },
    {
      ...{
        id: "administrative.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative10.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0e7490",
          fillOpacity: 0.2,
          lineColor: "#0e7490",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.10", "wfs") },
    },
    {
      ...{
        id: "administrative.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative11.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#a21caf",
          fillOpacity: 0.2,
          lineColor: "#a21caf",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.11", "wfs") },
    },
    {
      ...{
        id: "administrative.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative12.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#2563eb",
          fillOpacity: 0.2,
          lineColor: "#2563eb",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.12", "wfs") },
    },
    {
      ...{
        id: "administrative.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.administrative13.name",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#16a34a",
          fillOpacity: 0.2,
          lineColor: "#16a34a",
          lineWidth: 1.5,
        },
        labelAttribute: "navn",
      },
      source: { url: propertyMapUrl("administrative.13", "wfs") },
    },
    {
      ...{
        id: "planer.1",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer1.name",
        descriptionKey: "properties:map.layers.planer1.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_zonekort_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_zonekort_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer2.name",
        descriptionKey: "properties:map.layers.planer2.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplan_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplan_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.3",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer3.name",
        descriptionKey: "properties:map.layers.planer3.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplan_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplan_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.4",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer4.name",
        descriptionKey: "properties:map.layers.planer4.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplan_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplan_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.5",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer5.name",
        descriptionKey: "properties:map.layers.planer5.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplandelomraade_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplandelomraade_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.6",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer6.name",
        descriptionKey: "properties:map.layers.planer6.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplandelomraade_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplandelomraade_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.7",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer7.name",
        descriptionKey: "properties:map.layers.planer7.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lokalplandelomraade_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lokalplandelomraade_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.8",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer8.name",
        descriptionKey: "properties:map.layers.planer8.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_byggefelt_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_byggefelt_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.9",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer9.name",
        descriptionKey: "properties:map.layers.planer9.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_byggefelt_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_byggefelt_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.10",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer10.name",
        descriptionKey: "properties:map.layers.planer10.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_byggefelt_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_byggefelt_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.11",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer11.name",
        descriptionKey: "properties:map.layers.planer11.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplanramme_vedtaget_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplanramme_vedtaget_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.12",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer12.name",
        descriptionKey: "properties:map.layers.planer12.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplanramme_forslag_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplanramme_forslag_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.13",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer13.name",
        descriptionKey: "properties:map.layers.planer13.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplanramme_alle_aflyst_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplanramme_alle_aflyst_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.14",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer14.name",
        descriptionKey: "properties:map.layers.planer14.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplantillaeg_vedtaget_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplantillaeg_vedtaget_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.15",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer15.name",
        descriptionKey: "properties:map.layers.planer15.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplantillaeg_forslag_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplantillaeg_forslag_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.16",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer16.name",
        descriptionKey: "properties:map.layers.planer16.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kommuneplantillaeg_aflyst_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kommuneplantillaeg_aflyst_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.17",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer17.name",
        descriptionKey: "properties:map.layers.planer17.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_detailhandel_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_detailhandel_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.18",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer18.name",
        descriptionKey: "properties:map.layers.planer18.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_detailhandel_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_detailhandel_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.19",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer19.name",
        descriptionKey: "properties:map.layers.planer19.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_detailhandel_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_detailhandel_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.20",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer20.name",
        descriptionKey: "properties:map.layers.planer20.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oekologiskforbindelse_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oekologiskforbindelse_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.21",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer21.name",
        descriptionKey: "properties:map.layers.planer21.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oekologiskforbindelse_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oekologiskforbindelse_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.22",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer22.name",
        descriptionKey: "properties:map.layers.planer22.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oekologiskforbindelse_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oekologiskforbindelse_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.23",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer23.name",
        descriptionKey: "properties:map.layers.planer23.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_skovrejsningsomraade_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_skovrejsningsomraade_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.24",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer24.name",
        descriptionKey: "properties:map.layers.planer24.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_skovrejsningsomraade_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_skovrejsningsomraade_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.25",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer25.name",
        descriptionKey: "properties:map.layers.planer25.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_skovrejsningsomraade_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_skovrejsningsomraade_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.26",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer26.name",
        descriptionKey: "properties:map.layers.planer26.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.27",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer27.name",
        descriptionKey: "properties:map.layers.planer27.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.28",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer28.name",
        descriptionKey: "properties:map.layers.planer28.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_kulturhistoriskbevaringsvaerdi_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.29",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer29.name",
        descriptionKey: "properties:map.layers.planer29.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_bevaringsvaerdigelandskaber_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_bevaringsvaerdigelandskaber_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.30",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer30.name",
        descriptionKey: "properties:map.layers.planer30.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_bevaringsvaerdigelandskaber_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_bevaringsvaerdigelandskaber_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.31",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer31.name",
        descriptionKey: "properties:map.layers.planer31.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_bevaringsvaerdigelandskaber_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_bevaringsvaerdigelandskaber_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.32",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer32.name",
        descriptionKey: "properties:map.layers.planer32.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.33",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer33.name",
        descriptionKey: "properties:map.layers.planer33.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.34",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer34.name",
        descriptionKey: "properties:map.layers.planer34.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtlandbrugsomraade_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.35",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer35.name",
        descriptionKey: "properties:map.layers.planer35.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lavbundsareal_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lavbundsareal_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.36",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer36.name",
        descriptionKey: "properties:map.layers.planer36.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lavbundsareal_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lavbundsareal_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.37",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer37.name",
        descriptionKey: "properties:map.layers.planer37.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_lavbundsareal_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_lavbundsareal_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.38",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer38.name",
        descriptionKey: "properties:map.layers.planer38.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_stoerresammenhaengendelandskaber_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_stoerresammenhaengendelandskaber_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.39",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer39.name",
        descriptionKey: "properties:map.layers.planer39.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_stoerresammenhaengendelandskaber_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_stoerresammenhaengendelandskaber_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.40",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer40.name",
        descriptionKey: "properties:map.layers.planer40.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_stoerresammenhaengendelandskaber_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_stoerresammenhaengendelandskaber_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.41",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer41.name",
        descriptionKey: "properties:map.layers.planer41.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtkulturmiljoe_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtkulturmiljoe_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.42",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer42.name",
        descriptionKey: "properties:map.layers.planer42.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtkulturmiljoe_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtkulturmiljoe_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.43",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer43.name",
        descriptionKey: "properties:map.layers.planer43.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_vaerdifuldtkulturmiljoe_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_vaerdifuldtkulturmiljoe_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.44",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer44.name",
        descriptionKey: "properties:map.layers.planer44.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oversvoemerosion_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oversvoemerosion_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.45",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer45.name",
        descriptionKey: "properties:map.layers.planer45.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oversvoemerosion_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oversvoemerosion_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.46",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer46.name",
        descriptionKey: "properties:map.layers.planer46.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_oversvoemerosion_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_oversvoemerosion_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.47",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer47.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19vn_transportkorridorer",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19vn_transportkorridorer",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.48",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer48.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19vf_bykiler",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19vf_bykiler",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.49",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer49.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19va_haandflade",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19va_haandflade",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.50",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer50.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19va_byfingre",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19va_byfingre",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.51",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer51.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19va_ydre_landomr",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19va_ydre_landomr",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.52",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer52.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://gisp.bpst.dk/fingerplan19/wms",
          "fingerplan19:theme-fp19va_byomr_ovr",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://gisp.bpst.dk/fingerplan19/wms",
            "fingerplan19:theme-fp19va_byomr_ovr",
          ),
        },
      ],
    },
    {
      ...{
        id: "planer.53",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer53.name",
        descriptionKey: "properties:map.layers.planer53.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl("https://geocloud.vd.dk/NR/wms", "nr_byggelinjer"),
      },
      legendImages: [
        {
          url: wmsLegendUrl("https://geocloud.vd.dk/NR/wms", "nr_byggelinjer"),
        },
      ],
    },
    {
      ...{
        id: "planer.54",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.planer54.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_varmeplansomraade_vedtaget_v",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_varmeplansomraade_vedtaget_v",
          ),
        },
      ],
    },
    {
      ...{
        id: "Jordbunds_og_terraenforhold:Kvaelstofretention",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kvaelstofretention.name",
        descriptionKey: "properties:map.layers.kvaelstofretention.description",
        kind: "wms",
        legendColor: "#08E66C",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Jordbunds_og_terraenforhold:Kvaelstofretention",
        ),
      },
    },
    {
      ...{
        id: "theme-kulstofrige_lavbund_2022_kulstof2022_i0",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kulstof2022.name",
        descriptionKey: "properties:map.layers.kulstof2022.description",
        kind: "wms",
        legendColor: "#E28330",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=miljoegis-plangroendk_wms",
          "theme-kulstofrige_lavbund_2022_kulstof2022_i0",
        ),
      },
    },
    {
      ...{
        id: "tagflade_udst",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.solTagflader.name",
        descriptionKey: "properties:map.layers.solTagflader.description",
        kind: "wms",
        legendColor: "#F47D2A",
      },
      source: {
        url: wmsTileUrl("https://gisp.bpst.dk/ve/ows", "tagflade_udst"),
      },
    },
    {
      ...{
        id: "tage_udst",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.solBygninger.name",
        descriptionKey: "properties:map.layers.solBygninger.description",
        kind: "wms",
        legendColor: "#E36D13",
      },
      source: { url: wmsTileUrl("https://gisp.bpst.dk/ve/ows", "tage_udst") },
    },
    {
      ...{
        id: "markudledningskort:Markudledning2025_SEGES",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.markudledning2025.name",
        descriptionKey: "properties:map.layers.markudledning2025.description",
        kind: "wms",
        legendColor: "#239816",
      },
      source: {
        url: wmsTileUrl(
          "https://arld-extgeo.miljoeportal.dk/geoserver/wms",
          "markudledningskort:Markudledning2025_SEGES",
        ),
      },
    },
    {
      ...{
        id: "Jordbunds_og_terraenforhold:GLM_jorderosion",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.glmJorderosion.name",
        descriptionKey: "properties:map.layers.glmJorderosion.description",
        kind: "wms",
        legendColor: "#FFA54F",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Jordbunds_og_terraenforhold:GLM_jorderosion",
        ),
      },
    },
    {
      ...{
        id: "Jordbunds_og_terraenforhold:Vaadomraadepotentiale",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.vaadomraadepotentiale.name",
        descriptionKey:
          "properties:map.layers.vaadomraadepotentiale.description",
        kind: "wms",
        legendColor: "#F0CB58",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Jordbunds_og_terraenforhold:Vaadomraadepotentiale",
        ),
      },
    },
    {
      ...{
        id: "theme-vp3e2022-kystvande",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kystvandeAfgraensning.name",
        descriptionKey:
          "properties:map.layers.kystvandeAfgraensning.description",
        kind: "wms",
        legendColor: "#005AE3",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=vandrammedirektiv3-2022_wms",
          "theme-vp3e2022-kystvande",
        ),
      },
    },
    {
      ...restrictionPropertyLayer("bes_vandloeb"),
      ...{
        id: "bes_vandloeb",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.beskyttedeVandloeb.name",
        descriptionKey: "properties:map.layers.beskyttedeVandloeb.description",
      },
    },
    {
      ...restrictionPropertyLayer("bes_naturtyper"),
      ...{
        id: "bes_naturtyper",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.beskyttedeNaturtyper.name",
        descriptionKey:
          "properties:map.layers.beskyttedeNaturtyper.description",
      },
    },
    {
      ...{
        id: "foelsommeindvindingsomraader",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.foelsommeIndvindingsomraader.name",
        descriptionKey:
          "properties:map.layers.foelsommeIndvindingsomraader.description",
        kind: "wms",
        legendColor: "#E1B561",
      },
      source: {
        url: wmsTileUrl(
          "https://wfs2-miljoegis.mim.dk/grukos/ows",
          "foelsommeindvindingsomraader",
        ),
      },
    },
    {
      ...{
        id: "theme-indvindingsoplande_i_osd",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.indvindingsoplandeIOsd.name",
        descriptionKey:
          "properties:map.layers.indvindingsoplandeIOsd.description",
        kind: "wms",
        legendColor: "#005CE6",
      },
      source: {
        url: wmsTileUrl(
          "https://wfs2-miljoegis.mim.dk/grukos/ows",
          "theme-indvindingsoplande_i_osd",
        ),
      },
    },
    {
      ...{
        id: "grukos:indvindingsoplande_udenfor_osd",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.indvindingsoplandeUdenforOsd.name",
        descriptionKey:
          "properties:map.layers.indvindingsoplandeUdenforOsd.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#0E7490",
          fillOpacity: 0.2,
          lineColor: "#0E7490",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/grukos/ows",
          "grukos:indvindingsoplande_udenfor_osd",
        ),
      },
    },
    {
      ...{
        id: "grukos:saarbaregrundvandsdannendeomraader",
        role: "overlay",
        minZoom: 14,
        nameKey:
          "properties:map.layers.saarbareGrundvandsdannendeOmraader.name",
        descriptionKey:
          "properties:map.layers.saarbareGrundvandsdannendeOmraader.description",
        kind: "wfs",
        geometryType: "polygon",
        style: {
          fillColor: "#9333EA",
          fillOpacity: 0.2,
          lineColor: "#9333EA",
          lineWidth: 1.5,
        },
      },
      source: {
        url: wfsUrl(
          "https://wfs2-miljoegis.mim.dk/grukos/ows",
          "grukos:saarbaregrundvandsdannendeomraader",
        ),
      },
    },
    {
      ...{
        id: "theme-vp3e2022-kystvande_okotilstand_samlet",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kystvandeOekotilstand.name",
        descriptionKey:
          "properties:map.layers.kystvandeOekotilstand.description",
        kind: "wms",
        legendColor: "#EB820E",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=vandrammedirektiv3-2022_wms",
          "theme-vp3e2022-kystvande_okotilstand_samlet",
        ),
      },
    },
    {
      ...{
        id: "theme-vp3e2022-vandramme_oplande",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.lokaleTreparter.name",
        descriptionKey: "properties:map.layers.lokaleTreparter.description",
        kind: "wms",
        legendColor: "#005EE3",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=vandrammedirektiv3-2022_wms",
          "theme-vp3e2022-vandramme_oplande",
        ),
      },
    },
    {
      ...{
        id: "dn:arealreservation_20220928",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.beskyttetNatur30.name",
        descriptionKey: "properties:map.layers.beskyttetNatur30.description",
        kind: "wms",
        legendColor: "#4EC201",
      },
      source: {
        url: wmsTileUrl(
          "https://arld-extgeo.miljoeportal.dk/geoserver/wms",
          "dn:arealreservation_20220928",
        ),
      },
    },
    {
      ...{
        id: "mars:projekter_samlet",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.udtagningsprojekter.name",
        descriptionKey: "properties:map.layers.udtagningsprojekter.description",
        kind: "wms",
        legendColor: "#026A63",
      },
      source: {
        url: wmsTileUrl(
          "https://mars.sgav.dk/geo/wms",
          "mars:projekter_samlet",
        ),
      },
    },
    {
      ...{
        id: "mars:skitseprojekter",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.marsSkitseprojekter.name",
        descriptionKey: "properties:map.layers.marsSkitseprojekter.description",
        kind: "wms",
        legendColor: "#4E2209",
      },
      source: {
        url: wmsTileUrl("https://mars.sgav.dk/geo/wms", "mars:skitseprojekter"),
      },
    },
    {
      ...{
        id: "Vandprojekter:Kystvandoplande_VP2",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kystvandoplandeVp2.name",
        descriptionKey: "properties:map.layers.kystvandoplandeVp2.description",
        kind: "wms",
        legendColor: "#1157E1",
      },
      source: {
        url: wmsTileUrl(
          "https://geodata.fvm.dk/geoserver/ows",
          "Vandprojekter:Kystvandoplande_VP2",
        ),
      },
    },
    {
      ...{
        id: "mastedatabasen:MasteIBrug",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.mastedatabasen.name",
        descriptionKey: "properties:map.layers.mastedatabasen.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://mastedatabasen.dk/viskort/map.ashx",
          "mastedatabasen:MasteIBrug",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://mastedatabasen.dk/viskort/map.ashx",
            "mastedatabasen:MasteIBrug",
          ),
        },
      ],
    },
    {
      ...{
        id: "jupiter_boringer_ws",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.boringer.name",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://data.geus.dk/geusmap/ows/25832.jsp?mapname=jupiter&whoami=support@miljoeportal.dk",
          "jupiter_boringer_ws",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://data.geus.dk/geusmap/ows/25832.jsp?mapname=jupiter&whoami=support@miljoeportal.dk",
            "jupiter_boringer_ws",
          ),
        },
      ],
    },
    {
      ...{
        id: "grukos:drikkevandsinteresser",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.drikkevandsinteresser.name",
        descriptionKey:
          "properties:map.layers.drikkevandsinteresser.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://wfs2-miljoegis.mim.dk/grukos/ows",
          "grukos:drikkevandsinteresser",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://wfs2-miljoegis.mim.dk/grukos/ows",
            "grukos:drikkevandsinteresser",
          ),
        },
      ],
    },
    {
      ...{
        id: "RAASTOFOMRAADE",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.raastofomraader.name",
        descriptionKey: "properties:map.layers.raastofomraader.description",
        kind: "wms",
        legendColor: "#66FF00",
      },
      source: { url: propertyMapUrl("RAASTOFOMRAADE", "wms") },
    },
    {
      ...{
        id: "theme-gvkort_indsatsomraader_gvkort_indsatsomraader_i0",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.indsatsomraader.name",
        descriptionKey: "properties:map.layers.indsatsomraader.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=grundvand_wms",
          "theme-gvkort_indsatsomraader_gvkort_indsatsomraader_i0",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://miljoegis.mim.dk/wms?servicename=grundvand_wms",
            "theme-gvkort_indsatsomraader_gvkort_indsatsomraader_i0",
          ),
        },
      ],
    },
    {
      ...{
        id: "pdk:theme_pdk_naturbeskyttelsesomraade_aflyst",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.naturbeskyttelsesomraaderAflyst.name",
        descriptionKey:
          "properties:map.layers.naturbeskyttelsesomraaderAflyst.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_naturbeskyttelsesomraade_aflyst",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_naturbeskyttelsesomraade_aflyst",
          ),
        },
      ],
    },
    {
      ...{
        id: "pdk:theme_pdk_naturbeskyttelsesomraade_forslag",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.naturbeskyttelsesomraaderForslag.name",
        descriptionKey:
          "properties:map.layers.naturbeskyttelsesomraaderForslag.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_naturbeskyttelsesomraade_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_naturbeskyttelsesomraade_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "pdk:theme_pdk_naturbeskyttelsesomraade_vedtaget",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.naturbeskyttelsesomraaderVedtaget.name",
        descriptionKey:
          "properties:map.layers.naturbeskyttelsesomraaderVedtaget.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_naturbeskyttelsesomraade_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_naturbeskyttelsesomraade_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "bnbo",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.bnbo.name",
        descriptionKey: "properties:map.layers.bnbo.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl("https://wfs2-miljoegis.mim.dk/grukos/ows", "bnbo"),
      },
      legendImages: [
        {
          url: wmsLegendUrl("https://wfs2-miljoegis.mim.dk/grukos/ows", "bnbo"),
        },
      ],
    },
    {
      ...{
        id: "dai:status_bnbo",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.bnboStatus.name",
        descriptionKey: "properties:map.layers.bnboStatus.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms",
          "dai:status_bnbo",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms",
            "dai:status_bnbo",
          ),
        },
      ],
    },
    {
      ...{
        id: "PlanDK_KortNr",
        role: "overlay",
        minZoom: 15,
        nameKey: "properties:map.layers.planfikspunktLoebenummer.name",
        descriptionKey:
          "properties:map.layers.planfikspunktLoebenummer.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("PlanDK_KortNr", "wms") },
    },
    {
      ...{
        id: "HoejdeDK_KortNr",
        role: "overlay",
        minZoom: 15,
        nameKey: "properties:map.layers.hoejdefikspunktLoebenummer.name",
        descriptionKey:
          "properties:map.layers.hoejdefikspunktLoebenummer.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("HoejdeDK_KortNr", "wms") },
    },
    {
      ...{
        id: "PlanfikspunktDK",
        role: "overlay",
        minZoom: 15,
        nameKey: "properties:map.layers.planfikspunktFuldeNummer.name",
        descriptionKey:
          "properties:map.layers.planfikspunktFuldeNummer.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("PlanfikspunktDK", "wms") },
    },
    {
      ...{
        id: "HoejdefikspunktDK",
        role: "overlay",
        minZoom: 15,
        nameKey: "properties:map.layers.hoejdefikspunktFuldeNummer.name",
        descriptionKey:
          "properties:map.layers.hoejdefikspunktFuldeNummer.description",
        kind: "wms",
        legendColor: "#000000",
      },
      source: { url: propertyMapUrl("HoejdefikspunktDK", "wms") },
    },
    {
      ...{
        id: "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_forslag",
        role: "overlay",
        minZoom: 14,
        nameKey:
          "properties:map.layers.geologiskeBevaringsvaerdierForslag.name",
        descriptionKey:
          "properties:map.layers.geologiskeBevaringsvaerdierForslag.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_forslag",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_forslag",
          ),
        },
      ],
    },
    {
      ...{
        id: "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_vedtaget",
        role: "overlay",
        minZoom: 14,
        nameKey:
          "properties:map.layers.geologiskeBevaringsvaerdierVedtaget.name",
        descriptionKey:
          "properties:map.layers.geologiskeBevaringsvaerdierVedtaget.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_vedtaget",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_vedtaget",
          ),
        },
      ],
    },
    {
      ...{
        id: "dai:indsatsplaner",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.grundvandsbeskyttelse.name",
        descriptionKey:
          "properties:map.layers.grundvandsbeskyttelse.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms",
          "dai:indsatsplaner",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms",
            "dai:indsatsplaner",
          ),
        },
      ],
    },
    {
      ...{
        id: "knz:theme-knz-kystnaerhedszone-polygon",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.kystnaerhedszone.name",
        descriptionKey: "properties:map.layers.kystnaerhedszone.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://geoserver.plandata.dk/geoserver/wms",
          "knz:theme-knz-kystnaerhedszone-polygon",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://geoserver.plandata.dk/geoserver/wms",
            "knz:theme-knz-kystnaerhedszone-polygon",
          ),
        },
      ],
    },
    {
      ...{
        id: "theme-biodiversitet-raster-2021-bioscore_biodiversitet-2021_bioscore_i0",
        role: "overlay",
        minZoom: 14,
        nameKey: "properties:map.layers.bioscore.name",
        descriptionKey: "properties:map.layers.bioscore.description",
        kind: "wms",
      },
      source: {
        url: wmsTileUrl(
          "https://miljoegis.mim.dk/wms?servicename=miljoegis-plangroendk_wms",
          "theme-biodiversitet-raster-2021-bioscore_biodiversitet-2021_bioscore_i0",
        ),
      },
      legendImages: [
        {
          url: wmsLegendUrl(
            "https://miljoegis.mim.dk/wms?servicename=miljoegis-plangroendk_wms",
            "theme-biodiversitet-raster-2021-bioscore_biodiversitet-2021_bioscore_i0",
          ),
        },
      ],
    },
  ],
  groups: [
    {
      id: "backgrounds",
      labelKey: "properties:map.groups.backgrounds",
      layerIds: [
        "backgrounds.1",
        "backgrounds.2",
        "backgrounds.3",
        "backgrounds.4",
      ],
    },
    {
      id: "ortofoto",
      labelKey: "properties:map.groups.ortofoto",
      layerIds: [
        "ortofoto.1",
        "ortofoto.2",
        "ortofoto.3",
        "ortofoto.4",
        "ortofoto.5",
        "ortofoto.6",
        "ortofoto.7",
        "ortofoto.8",
        "ortofoto.9",
        "ortofoto.10",
      ],
    },
    {
      id: "geodanmark",
      labelKey: "properties:map.groups.geodanmark",
      layerIds: [],
      subgroups: [
        {
          id: "Bygning",
          labelKey: "properties:map.subgroups.Bygning",
          layerIds: ["geodanmark.1"],
        },
        {
          id: "Hydro",
          labelKey: "properties:map.subgroups.Hydro",
          layerIds: [
            "geodanmark.2",
            "geodanmark.3",
            "geodanmark.4",
            "geodanmark.5",
            "geodanmark.6",
            "geodanmark.7",
            "geodanmark.8",
          ],
        },
        {
          id: "Natur",
          labelKey: "properties:map.subgroups.Natur",
          layerIds: [
            "geodanmark.9",
            "geodanmark.10",
            "geodanmark.11",
            "geodanmark.12",
            "geodanmark.13",
            "geodanmark.14",
            "geodanmark.15",
            "geodanmark.16",
            "geodanmark.17",
            "geodanmark.18",
            "geodanmark.19",
          ],
        },
        {
          id: "Teknik",
          labelKey: "properties:map.subgroups.Teknik",
          layerIds: [
            "geodanmark.20",
            "geodanmark.21",
            "geodanmark.22",
            "geodanmark.23",
            "geodanmark.24",
            "geodanmark.25",
            "geodanmark.26",
            "geodanmark.27",
            "geodanmark.28",
            "geodanmark.29",
            "geodanmark.30",
            "geodanmark.31",
            "geodanmark.32",
            "geodanmark.33",
            "geodanmark.34",
            "geodanmark.35",
            "geodanmark.36",
            "geodanmark.37",
            "geodanmark.38",
          ],
        },
        {
          id: "Trafik",
          labelKey: "properties:map.subgroups.Trafik",
          layerIds: [
            "geodanmark.39",
            "geodanmark.40",
            "geodanmark.41",
            "geodanmark.42",
            "geodanmark.43",
            "geodanmark.44",
            "geodanmark.45",
            "geodanmark.46",
          ],
        },
        {
          id: "Øvrige",
          labelKey: "properties:map.subgroups.Øvrige",
          layerIds: [
            "geodanmark.47",
            "geodanmark.48",
            "geodanmark.49",
            "geodanmark.50",
            "geodanmark.51",
            "geodanmark.52",
            "geodanmark.53",
            "geodanmark.54",
            "geodanmark.55",
            "geodanmark.56",
            "geodanmark.57",
            "geodanmark.58",
            "geodanmark.59",
            "geodanmark.61",
            "geodanmark.62",
            "geodanmark.63",
            "geodanmark.64",
            "geodanmark.65",
            "geodanmark.66",
          ],
        },
      ],
    },
    {
      id: "roads",
      labelKey: "properties:map.groups.roads",
      layerIds: ["roads.1", "roads.2", "roads.3"],
    },
    {
      id: "elevation",
      labelKey: "properties:map.groups.elevation",
      layerIds: [
        "elevation.1",
        "elevation.2",
        "elevation.3",
        "elevation.4",
        "elevation.5",
        "elevation.6",
      ],
    },
    {
      id: "protection",
      labelKey: "properties:map.groups.protection",
      layerIds: [
        "protection.1",
        "protection.2",
        "protection.3",
        "protection.4",
        "protection.5",
        "protection.6",
        "protection.7",
        "protection.10",
        "protection.11",
        "protection.12",
        "protection.13",
        "protection.14",
        "protection.15",
        "protection.16",
        "protection.17",
        "protection.18",
        "protection.19",
        "protection.20",
        "protection.21",
        "bes_vandloeb",
        "bes_naturtyper",
      ],
    },
    {
      id: "pollution",
      labelKey: "properties:map.groups.pollution",
      layerIds: [
        "pollution.1",
        "pollution.2",
        "pollution.3",
        "pollution.4",
        "pollution.5",
      ],
    },
    {
      id: "noise",
      labelKey: "properties:map.groups.noise",
      layerIds: [
        "noise.1",
        "noise.2",
        "noise.3",
        "noise.4",
        "noise.5",
        "noise.6",
        "noise.7",
        "noise.8",
        "noise.9",
        "noise.10",
        "noise.11",
        "noise.12",
        "noise.13",
        "noise.14",
      ],
    },
    {
      id: "agriculture",
      labelKey: "properties:map.groups.agriculture",
      layerIds: [
        "agriculture.1",
        "agriculture.2",
        "agriculture.3",
        "agriculture.4",
        "agriculture.5",
        "agriculture.6",
        "agriculture.7",
        "agriculture.8",
        "agriculture.9",
        "agriculture.10",
        "agriculture.11",
        "agriculture.12",
        "agriculture.13",
        "agriculture.14",
        "agriculture.15",
        "agriculture.16",
        "agriculture.17",
        "agriculture.18",
        "agriculture.19",
        "agriculture.20",
        "agriculture.21",
        "agriculture.22",
      ],
    },
    {
      id: "heritage",
      labelKey: "properties:map.groups.heritage",
      layerIds: [
        "heritage.1",
        "heritage.2",
        "heritage.3",
        "heritage.4",
        "heritage.5",
        "heritage.6",
        "heritage.7",
        "heritage.8",
        "heritage.9",
        "heritage.10",
        "heritage.11",
        "heritage.12",
      ],
    },
    {
      id: "utilities",
      labelKey: "properties:map.groups.utilities",
      layerIds: [
        "utilities.1",
        "utilities.2",
        "utilities.3",
        "utilities.4",
        "utilities.5",
        "utilities.6",
        "utilities.7",
      ],
    },
    {
      id: "administrative",
      labelKey: "properties:map.groups.administrative",
      layerIds: [
        "administrative.1",
        "administrative.2",
        "administrative.3",
        "administrative.4",
        "administrative.5",
        "administrative.6",
        "administrative.7",
        "administrative.8",
        "administrative.9",
        "administrative.10",
        "administrative.11",
        "administrative.12",
        "administrative.13",
      ],
    },
    {
      id: "planer",
      labelKey: "properties:map.groups.planer",
      layerIds: ["planer.1"],
      subgroups: [
        {
          id: "Lokalplaner",
          labelKey: "properties:map.subgroups.Lokalplaner",
          layerIds: ["planer.2", "planer.3", "planer.4"],
        },
        {
          id: "Delområder",
          labelKey: "properties:map.subgroups.Delområder",
          layerIds: ["planer.5", "planer.6", "planer.7"],
        },
        {
          id: "Byggefelter",
          labelKey: "properties:map.subgroups.Byggefelter",
          layerIds: ["planer.8", "planer.9", "planer.10"],
        },
        {
          id: "Kommuneplanrammer",
          labelKey: "properties:map.subgroups.Kommuneplanrammer",
          layerIds: ["planer.11", "planer.12", "planer.13"],
        },
        {
          id: "Kommuneplantillæg",
          labelKey: "properties:map.subgroups.Kommuneplantillæg",
          layerIds: ["planer.14", "planer.15", "planer.16"],
        },
        {
          id: "Retningslinjer",
          labelKey: "properties:map.subgroups.Retningslinjer",
          layerIds: [
            "planer.17",
            "planer.18",
            "planer.19",
            "planer.20",
            "planer.21",
            "planer.22",
            "planer.23",
            "planer.24",
            "planer.25",
            "planer.26",
            "planer.27",
            "planer.28",
            "planer.29",
            "planer.30",
            "planer.31",
            "planer.32",
            "planer.33",
            "planer.34",
            "planer.35",
            "planer.36",
            "planer.37",
            "planer.38",
            "planer.39",
            "planer.40",
            "planer.41",
            "planer.42",
            "planer.43",
            "planer.44",
            "planer.45",
            "planer.46",
          ],
        },
        {
          id: "Fingerplan",
          labelKey: "properties:map.subgroups.Fingerplan",
          layerIds: [
            "planer.47",
            "planer.48",
            "planer.49",
            "planer.50",
            "planer.51",
            "planer.52",
          ],
        },
        {
          id: "Øvrige",
          labelKey: "properties:map.subgroups.Øvrige",
          layerIds: ["planer.53", "planer.54"],
        },
      ],
    },
    {
      id: "esg",
      labelKey: "properties:map.groups.esg",
      layerIds: [
        "Jordbunds_og_terraenforhold:Kvaelstofretention",
        "theme-kulstofrige_lavbund_2022_kulstof2022_i0",
        "tagflade_udst",
        "tage_udst",
        "markudledningskort:Markudledning2025_SEGES",
        "Jordbunds_og_terraenforhold:GLM_jorderosion",
        "Jordbunds_og_terraenforhold:Vaadomraadepotentiale",
        "theme-vp3e2022-kystvande",
        "bes_vandloeb",
        "bes_naturtyper",
        "foelsommeindvindingsomraader",
        "theme-indvindingsoplande_i_osd",
        "grukos:indvindingsoplande_udenfor_osd",
        "grukos:saarbaregrundvandsdannendeomraader",
        "theme-vp3e2022-kystvande_okotilstand_samlet",
        "theme-vp3e2022-vandramme_oplande",
        "dn:arealreservation_20220928",
        "mars:projekter_samlet",
        "mars:skitseprojekter",
        "Vandprojekter:Kystvandoplande_VP2",
      ],
    },
    {
      id: "oevrige",
      labelKey: "properties:map.groups.oevrige",
      layerIds: [
        "mastedatabasen:MasteIBrug",
        "jupiter_boringer_ws",
        "grukos:drikkevandsinteresser",
        "RAASTOFOMRAADE",
        "theme-gvkort_indsatsomraader_gvkort_indsatsomraader_i0",
        "pdk:theme_pdk_naturbeskyttelsesomraade_aflyst",
        "pdk:theme_pdk_naturbeskyttelsesomraade_forslag",
        "pdk:theme_pdk_naturbeskyttelsesomraade_vedtaget",
        "bnbo",
        "dai:status_bnbo",
        "PlanDK_KortNr",
        "HoejdeDK_KortNr",
        "PlanfikspunktDK",
        "HoejdefikspunktDK",
        "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_forslag",
        "pdk:theme_pdk_specifikgeologiskbevaringsvaerdi_vedtaget",
        "dai:indsatsplaner",
        "knz:theme-knz-kystnaerhedszone-polygon",
        "theme-biodiversitet-raster-2021-bioscore_biodiversitet-2021_bioscore_i0",
      ],
    },
  ],
};
